// creditCostEstimator — Pre-execution cost-estimation i credits per kind.
//
// Använder conversionRates + blockExecutorRegistry + performanceProfiler-
// historia för att ge en sammansatt estimate.

import type { CostEstimate, CreditKind } from '@/lib/creditTypes';
import {
  tokensToCredits,
  bytesToStorageCredits,
  secondsToComputeCredits,
  exportsToCredits,
  DEFAULT_RATES,
  type ConversionRates,
} from '@/lib/creditConversionRates';
import { BLOCK_EXECUTORS } from '@/lib/blockExecutorRegistry';
import type { WorkflowState } from '@/types/workflow';

export interface AiCostInput {
  promptTokens: number;
  completionTokens: number;
  model: string;
}

export function estimateAiCost(
  input: AiCostInput,
  rates: ConversionRates = DEFAULT_RATES,
): CostEstimate {
  const total = input.promptTokens + input.completionTokens;
  const aiCredits = tokensToCredits(total, input.model, rates);

  return {
    credits: { ai: round2(aiCredits) },
    confidence: input.model in rates.tokensPerCreditByModel ? 0.95 : 0.7,
    breakdown: [
      {
        kind: 'ai',
        amount: round2(aiCredits),
        reason: `${total} tokens (${input.model})`,
      },
    ],
  };
}

export interface WorkflowRunCostInput {
  workflow: WorkflowState;
  /** Estimated total duration i sek. */
  estimatedDurationSec?: number;
  cpuClass?: string;
}

export function estimateWorkflowRunCost(
  input: WorkflowRunCostInput,
  rates: ConversionRates = DEFAULT_RATES,
): CostEstimate {
  const breakdown: CostEstimate['breakdown'] = [];
  const credits: Partial<Record<CreditKind, number>> = {};

  let totalComputeSeconds = input.estimatedDurationSec ?? 0;
  let connectorCalls = 0;
  let webhookDeliveries = 0;

  for (const block of input.workflow.blocks) {
    const meta = BLOCK_EXECUTORS[block.type];
    if (!meta) continue;
    totalComputeSeconds += (meta.defaultTimeoutMs / 1000) * 0.2;

    if (block.type === 'webhook') webhookDeliveries += 1;
    if (block.type === 'create-user' || block.type === 'disable-mailbox'
        || block.type === 'delete-m365' || block.type === 'add-to-group') {
      connectorCalls += 1;
    }
  }

  if (totalComputeSeconds > 0) {
    const c = secondsToComputeCredits(totalComputeSeconds, input.cpuClass ?? 'medium', rates);
    credits.compute = round2(c);
    breakdown.push({
      kind: 'compute',
      amount: round2(c),
      reason: `${totalComputeSeconds.toFixed(1)}s compute (${input.cpuClass ?? 'medium'})`,
    });
  }

  if (connectorCalls > 0) {
    const c = connectorCalls / rates.callsPerCredit;
    credits.connector = round2(c);
    breakdown.push({
      kind: 'connector',
      amount: round2(c),
      reason: `${connectorCalls} connector-call(s)`,
    });
  }

  if (webhookDeliveries > 0) {
    const c = webhookDeliveries / rates.deliveriesPerCredit;
    credits.webhook = round2(c);
    breakdown.push({
      kind: 'webhook',
      amount: round2(c),
      reason: `${webhookDeliveries} webhook-delivery/-ies`,
    });
  }

  const confidence = input.estimatedDurationSec !== undefined ? 0.85 : 0.6;
  return { credits, confidence, breakdown };
}

export function estimateExportCost(
  kind: 'pdf' | 'csv' | 'png' | 'bpmn' | 'terraform' | 'airflow',
  sizeBytes: number,
  rates: ConversionRates = DEFAULT_RATES,
): CostEstimate {
  const exportCost = exportsToCredits(1, rates);
  const storageCost = bytesToStorageCredits(sizeBytes, 1, rates);
  return {
    credits: {
      export: round2(exportCost),
      storage: round2(storageCost),
    },
    confidence: 0.9,
    breakdown: [
      { kind: 'export', amount: round2(exportCost), reason: `1 ${kind}-export` },
      { kind: 'storage', amount: round2(storageCost), reason: `${sizeBytes} B × 1 dag` },
    ],
  };
}

export function totalCreditsOf(estimate: CostEstimate): number {
  return Object.values(estimate.credits).reduce((sum, v) => sum + (v ?? 0), 0);
}

export function mergeEstimates(
  a: CostEstimate,
  b: CostEstimate,
): CostEstimate {
  const credits: Partial<Record<CreditKind, number>> = {};
  const allKinds = new Set<CreditKind>([
    ...(Object.keys(a.credits) as CreditKind[]),
    ...(Object.keys(b.credits) as CreditKind[]),
  ]);
  for (const kind of allKinds) {
    credits[kind] = round2((a.credits[kind] ?? 0) + (b.credits[kind] ?? 0));
  }
  return {
    credits,
    confidence: Math.min(a.confidence, b.confidence),
    breakdown: [...a.breakdown, ...b.breakdown],
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
